// 연속 처치(폭주) HUD(COMBAT-FEEL 2.5). view.rampage와 Ev.KillChain/Ev.Rampage를 읽기만 한다.
// 화면 왼쪽 가장자리(높이 68% 부근: 위 목표 패널·요령 토스트와 아래 조이스틱 사이)에 얇게: 사슬 수, 남은 3초 창 세로 막대, 다음 보상(5마리마다)까지 5칸 진행. 플레이 영역(가운데·영웅 앞길)은 비운다.
// 섬광 안전: 크기 튕김과 페이드만 쓰고 밝기 번쩍임은 없다. 프레임 루프에서는 값이 바뀔 때만 DOM을 쓴다(막대는 5% 단위).
import { RAMPAGE, STYLE_RANK } from '../shared/config';
import { Ev, type GameEvent } from '../shared/events';
import type { GameView } from '../shared/types';
import { C, FONT, FONT_NUM, TEXT_OUTLINE, el } from './dom';
import { newRampageHudState, rampageHudState, sameRampageState, styleRankLetter, type RampageHudState } from './rampageModel';

/** 가장자리 배지 폭(디자인 px)과 막대 높이 */
const BAR_H = 90;

export class RampageHud {
  private readonly root: HTMLDivElement;
  private readonly count: HTMLSpanElement;
  private readonly fill: HTMLDivElement;
  private readonly pips: HTMLSpanElement[] = [];
  private readonly nextText: HTMLDivElement;
  private readonly buff: HTMLDivElement;
  private readonly banner: HTMLDivElement;
  /** 스타일 랭크(COMBAT-FEEL 2.14): 사슬 숫자 오른쪽의 큰 글자와 승급 링 */
  private readonly rankBox: HTMLDivElement;
  private readonly rankLetter: HTMLSpanElement;
  private readonly rankRing: HTMLDivElement;
  private readonly cur: RampageHudState = newRampageHudState();
  private readonly shown: RampageHudState = { ...newRampageHudState(), visible: true, chain: -1 };
  private lastScale = -1;
  /** 동작 줄이기(COMBAT-FEEL 5.1): 켜져 있으면 크기·이동 없이 불투명도만 바꾸고, 설정이 바뀌면 진행 중인 효과를 취소한다 */
  private readonly motionQuery: MediaQueryList | null = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;

  constructor(parent: HTMLElement, private readonly view: GameView, private readonly touch = false) {
    // 터치: 왼쪽 띠(목표·자동·콤보·요령) 아래로 — 68%에서는 요령 토스트와 겹쳤다(2026-10-27)
    this.root = el('div', 'position:absolute;left:5px;top:' + (touch ? 84 : 68) + '%;display:none;transform-origin:0 50%;pointer-events:none;font-family:' + FONT + ';', parent);
    this.root.dataset.testid = 'rampage-hud';
    const row = el('div', 'display:flex;align-items:stretch;gap:5px;', this.root);
    // 남은 창: 세로 막대(아래에서 줄어든다)
    const track = el('div', 'position:relative;width:8px;height:' + BAR_H + 'px;border-radius:4px;background:rgba(10,14,28,.62);border:1px solid rgba(242,193,78,.55);overflow:hidden;', row);
    this.fill = el('div', 'position:absolute;left:0;right:0;bottom:0;height:100%;background:linear-gradient(180deg,#ffe28a,#e0861c);transform-origin:50% 100%;', track);
    const col = el('div', 'display:flex;flex-direction:column;justify-content:center;gap:3px;', row);
    // HUD 정리(2026-10-27, 대표님 "콤보 화면보다 액션"): 터치에서는 이름표·다음 보상 문구 없이 숫자·창·칸만 작게
    const label = el('div', 'font:800 16px ' + FONT + ';color:' + C.gold + ';letter-spacing:.5px;white-space:nowrap;' + TEXT_OUTLINE, col, '연속 처치');
    if (touch) label.style.display = 'none';
    this.count = el('span', 'font:900 44px ' + FONT_NUM + ';font-style:italic;line-height:1;color:#fff;-webkit-text-stroke:1.5px #4a1500;display:inline-block;transform-origin:0 70%;' + TEXT_OUTLINE, col, '0');
    // 스타일 랭크: 화면 위쪽 콤보 표시 바로 오른쪽(Hud.layoutCombo가 placeRank로 위치를 정한다, COMBAT-FEEL 2.14)
    this.rankBox = el('div', 'position:absolute;left:0;top:0;display:none;width:84px;height:84px;transform-origin:0 0;pointer-events:none;', parent);
    this.rankBox.dataset.testid = 'style-rank';
    this.rankRing = el('div', 'position:absolute;left:50%;top:50%;width:74px;height:74px;margin:-37px 0 0 -37px;border-radius:50%;border:3px solid ' + C.gold + ';opacity:0;', this.rankBox);
    this.rankLetter = el('span', 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font:900 64px ' + FONT_NUM + ';font-style:italic;color:#ead6ff;-webkit-text-stroke:2.5px #24063f;' + TEXT_OUTLINE, this.rankBox, '');
    // 다음 보상까지 5칸
    const pipRow = el('div', 'display:flex;gap:3px;', col);
    for (let i = 0; i < RAMPAGE.every; i++) this.pips.push(el('span', 'width:11px;height:7px;border-radius:2px;border:1px solid rgba(242,193,78,.7);background:rgba(10,14,28,.6);', pipRow));
    this.nextText = el('div', 'font:700 15px ' + FONT + ';color:#e8eeff;white-space:nowrap;' + TEXT_OUTLINE, col, '');
    if (touch) this.nextText.style.display = 'none';
    this.buff = el('div', 'font:800 15px ' + FONT + ';color:#7ff0a8;white-space:nowrap;display:none;' + TEXT_OUTLINE, col, '이동 속도 +10%');
    // 단계 달성 문구(짧게 떠올랐다 사라진다)
    this.banner = el('div', 'position:absolute;left:0;bottom:100%;margin-bottom:4px;font:900 20px ' + FONT_NUM + ';font-style:italic;color:' + C.gold + ';white-space:nowrap;opacity:0;' + TEXT_OUTLINE, this.root, '');
    this.popFx = RampageHud.idle(this.count.animate([{ transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { duration: 120, easing: 'ease-out' }));
    this.popCalm = RampageHud.idle(this.count.animate([{ opacity: 0.6 }, { opacity: 1 }], { duration: 120, easing: 'ease-out' }));
    this.bannerFx = RampageHud.idle(this.banner.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: 1, transform: 'translateY(0)', offset: 0.6 }, { opacity: 0, transform: 'translateY(-8px)' }], { duration: 1300, easing: 'ease-out' }));
    this.bannerCalm = RampageHud.idle(this.banner.animate([{ opacity: 1 }, { opacity: 1, offset: 0.6 }, { opacity: 0 }], { duration: 1300, easing: 'ease-out' }));
    // 랭크 승급 450ms(측정: 링 폭발 50 → 입자 흩어짐 250 → 정착 150). 밝기 번쩍임 없이 크기·흐림·불투명도만 쓴다
    const [burst, dissolve, settle] = STYLE_RANK.promoMs, total = burst + dissolve + settle, o1 = burst / total, o2 = (burst + dissolve) / total;
    this.rankRingFx = RampageHud.idle(this.rankRing.animate([{ opacity: 0.9, transform: 'scale(.4)' }, { opacity: 0.9, transform: 'scale(1.5)', offset: o1 }, { opacity: 0, transform: 'scale(1.9)', offset: o2 }, { opacity: 0.6, transform: 'scale(1)', offset: o2 + 0.01 }, { opacity: 0, transform: 'scale(1.15)' }], { duration: total, easing: 'ease-out' }));
    this.rankLetterFx = RampageHud.idle(this.rankLetter.animate([{ opacity: 0, transform: 'scale(1.9)', filter: 'blur(6px)' }, { opacity: 0.35, transform: 'scale(1.8)', filter: 'blur(6px)', offset: o1 }, { opacity: 1, transform: 'scale(1)', filter: 'blur(0px)', offset: o2 }, { opacity: 1, transform: 'scale(1)', filter: 'blur(0px)' }], { duration: total, easing: 'ease-out' }));
    this.rankCalm = RampageHud.idle(this.rankLetter.animate([{ opacity: 0 }, { opacity: 1, offset: o2 }, { opacity: 1 }], { duration: total, easing: 'linear' }));
    // 동작 줄이기 설정이 바뀌면 진행 중인 효과를 취소한다
    this.motionQuery?.addEventListener?.('change', () => { this.popFx.cancel(); this.popCalm.cancel(); this.bannerFx.cancel(); this.bannerCalm.cancel(); this.rankRingFx.cancel(); this.rankLetterFx.cancel(); this.rankCalm.cancel(); });
  }

  /**
   * 효과 애니메이션은 생성자에서 한 번만 만들고(동작 줄이기 켬·끔 두 벌), 이벤트에서는 처음으로 감아 다시 튼다(COMBAT-FEEL 5.1).
   * 광역 처치로 KillChain이 한 프레임에 수십 번 와도 새 Animation·키프레임 배열을 만들지 않는다
   */
  private readonly popFx: Animation;
  private readonly popCalm: Animation;
  private readonly bannerFx: Animation;
  private readonly bannerCalm: Animation;
  private readonly rankRingFx: Animation;
  private readonly rankLetterFx: Animation;
  private readonly rankCalm: Animation;

  private static idle(a: Animation): Animation { a.cancel(); return a; }
  private static replay(a: Animation): void { a.cancel(); a.play(); }

  onEvent(e: GameEvent): void {
    const calm = this.motionQuery?.matches === true;
    if (e.type === Ev.KillChain) {
      // 처치마다 숫자가 살짝 튄다(크기만, 밝기 변화 없음). 동작 줄이기면 불투명도만
      (calm ? this.popFx : this.popCalm).cancel();
      RampageHud.replay(calm ? this.popCalm : this.popFx);
    } else if (e.type === Ev.Rampage) {
      this.banner.textContent = '폭주 ' + e.b + '단계!';
      (calm ? this.bannerFx : this.bannerCalm).cancel();
      RampageHud.replay(calm ? this.bannerCalm : this.bannerFx);
    } else if (e.type === Ev.StyleRank) {
      // 글자는 승급 순간에 바꾼다(update보다 이벤트가 먼저 와도 새 등급으로 연출)
      this.rankLetter.textContent = styleRankLetter(e.a);
      this.rankRingFx.cancel(); this.rankLetterFx.cancel(); this.rankCalm.cancel();
      if (calm) RampageHud.replay(this.rankCalm); else { RampageHud.replay(this.rankRingFx); RampageHud.replay(this.rankLetterFx); }
    }
  }

  /** u: 디자인 배율. live: 던전 전투 중 */
  update(u: number, live: boolean): void {
    const st = rampageHudState(this.cur, this.view.rampage, live);
    const sh = this.shown;
    if (sameRampageState(st, sh)) { this.scale(u); return; }
    if (st.visible !== sh.visible) {
      this.root.style.display = st.visible ? 'block' : 'none';
      // 새로 보일 때는 모든 칸을 다시 쓴다(숨겨 있는 동안 건너뛴 값 때문에 낡은 글자가 남지 않게)
      if (st.visible) { sh.chain = sh.windowStep = sh.pips = sh.next = sh.rank = -1; sh.buffOn = !st.buffOn; }
    }
    if (st.visible) {
      if (st.chain !== sh.chain) this.count.textContent = String(st.chain);
      if (st.windowStep !== sh.windowStep) this.fill.style.transform = 'scaleY(' + (st.windowStep / 20).toFixed(2) + ')';
      if (st.pips !== sh.pips) for (let i = 0; i < this.pips.length; i++) this.pips[i].style.background = i < st.pips ? C.gold : 'rgba(10,14,28,.6)';
      if (st.next !== sh.next) this.nextText.textContent = '다음 보상까지 ' + st.next;
      if (st.buffOn !== sh.buffOn) this.buff.style.display = st.buffOn ? 'block' : 'none';
    }
    // 랭크는 배지 밖(상단)에 있으므로 배지가 숨을 때도 직접 숨긴다(보이지 않으면 st.rank = 0)
    if (st.rank !== sh.rank) {
      // 터치: 큰 랭크 글자는 왼쪽 띠를 가려 숨긴다(2026-10-27 HUD 정리) — 랭크 효과·승급 링은 PC에서만
      this.rankBox.style.display = st.rank > 0 && !this.touch ? 'block' : 'none';
      this.rankLetter.textContent = styleRankLetter(st.rank);
      // 사슬이 끊겨 등급이 내려가면 진행 중인 승급 연출을 지운다(낡은 링이 남지 않게)
      if (st.rank < sh.rank) { this.rankRingFx.cancel(); this.rankLetterFx.cancel(); this.rankCalm.cancel(); }
    }
    sh.visible = st.visible; sh.chain = st.chain; sh.windowStep = st.windowStep; sh.pips = st.pips; sh.next = st.next; sh.buffOn = st.buffOn; sh.rank = st.rank;
    this.scale(u);
  }

  private scale(u: number): void {
    // 폰에서도 글자가 읽히도록 배율 하한 0.8. 가장 작은 글자를 15px로 두어 0.8배에서도 12px 이상(본문 최소 12px)
    const s = this.touch ? Math.max(u * 0.85, 0.8) : Math.max(u, 0.8);
    if (s === this.lastScale) return;
    this.lastScale = s;
    this.root.style.transform = 'translateY(-50%) scale(' + s.toFixed(3) + ')';
    // 랭크 글자는 콤보와 같이 작게(2026-10-27): 84px 상자를 0.65배
    this.rankBox.style.transform = 'scale(' + (Math.min(1, s) * 0.65).toFixed(3) + ')';
  }

  /** 스타일 랭크 위치(px): 콤보 띠 오른쪽 위 모서리. 레이아웃이 바뀔 때만 부른다 */
  placeRank(x: number, y: number): void {
    this.rankBox.style.left = Math.round(x) + 'px';
    this.rankBox.style.top = Math.round(y) + 'px';
  }
}
